import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import { FormControl, InputLabel, List, ListItem, ListItemText, ListItemButton, OutlinedInput, Select, Backdrop, CircularProgress } from "@mui/material";
import Chip from "@mui/material/Chip";
import MenuItem from "@mui/material/MenuItem";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import Box from "@mui/material/Box"

import { withTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { findIndex, isEmpty, cloneDeep } from "lodash";

import itemHrchyService from "@plannel/services/data/item-hrchy-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import Snackbar from "@plannel/components/Snackbar";
import Dialog from "@plannel/components/Dialog"
import DataState from "@plannel/components/aggrid/DataState"
import DefaultGridSetting from "@plannel/components/aggrid/DefaultGridSetting"
import GridUtils from "@plannel/components/aggrid/GridUtils";
import ExcelExportButton from "@plannel/components/ExcelExportButton";
import TextFieldSearch from "@plannel/components/TextFieldSearch";
import {AddButton, RemoveButton, SaveButton, SettingsButton, FilterButton} from "@plannel/components/ActionIconButton";
import reduxUtil from "@plannel/utils/redux-util";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "../utils/color-util";
import CodeUtil from "@plannel/pages/utils/code-util";
import FilterContainer from "@plannel/components/layout/FilterContainer";
import AdvancedFilter from "@plannel/components/filter/AdvancedFilter";
import ResizableFilterPanel from "@plannel/components/filter/ResizableFilterPanel";
import RatioRangeSettings from "@plannel/components/modal/RatioRangeSettings";
import {red} from "@mui/material/colors";
import NumberUtil from "@plannel/utils/number-util";

const { snackBarMessageColor } = ColorUtil;
const ItemHrchyMaster = ({ t, viewName, title }) => {
  const reduxViewState = reduxUtil.getViewState(viewName);
  const reduxDispatch = useDispatch();

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [progressOn, setProgressOn] = useState(false);

  const gridRef = useRef();

  const searchCode = useRef(reduxViewState?.searchCode ?? "");
  const [selectLevelDialog, setSelectLevelDialog] = useState(false);
  const [currentHrchy, setCurrentHrchy] = useState({ hrchyLv: 1 });

  const [hrchyLvConfigs, setHrchyLvConfigs] = useState([]);
  const [hrchyGrpConfigs, setHrchyGrpConfigs] = useState([]);
  const [itemHrchies, setItemHrchies] = useState([]);

  const [filteredItemHrchies, setFilteredItemHrchies] = useState([]);
  const [hrchyGrp, setHrchyGrp] = useState([]);
  const [selectedHrchyGrp, setSelectedHrchyGrp] = useState([]);
  const [selectedDataHrchyGrp, setSelectedDataHrchyGrp] = useState([]);
  const [hrchyGrpSelectorOpen, setHrchyGrpSelectorOpen] = useState(false);
  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [planConfig, setPlanConfig] = useState({});
  const [advancedFilters, setAdvancedFilters] = useState(reduxViewState?.advancedFilters ? reduxViewState.advancedFilters : null);
  const [openFilter, setOpenFilter] = useState(false);
  const [advancedFilterColor, setAdvancedFilterColor] = useState(!isEmpty(reduxViewState?.advancedFilters) ? "info" : "default");

  const defaultGridMemo = useMemo(() => {
    return DefaultGridSetting({ title, viewName })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const retrievePlanConfig = () => {
    const param = {
      moduleCd: CodeUtil.module.DP,
      configCd: "ITEM_HRCHY_RATIO_RANGE",
      activeFlg: true,
    };

    planConfigService.getConfigCd(param)
      .then(response => {
        if (response.data) {
          const planConfigData = response.data;
          setPlanConfig(planConfigData);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const autoGroupColumnMemo = useMemo(() => {
    return {
      headerName: t("hrchyCd"),
      minWidth: 250,
      cellClass: 'stringType',
      cellRendererParams: {
        suppressCount: true,
      },
      valueSetter: (params) => {
        params.data.hrchyPath.pop();
        params.data.hrchyPath.push(params.newValue);
        params.data.hrchyCd = params.newValue;
      },
      valueGetter: (params) => {
        return params.data ? params.data.hrchyCd : params.node.key;
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemHrchies, t]);

  const getRequestParams = () => {
    const params = {}
    const searchFilters = { activeFlg: false };

    if (searchCode.current) {
      searchFilters.hierarchyCd = searchCode.current;
    }

    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      params.advancedFilters = advancedFilters;
    }
    params.searchFilters = searchFilters;

    return params;
  }

  const refreshGridByFilter = (dataResults) => {
    if (selectedHrchyGrp.length > 0) {
      setFilteredItemHrchies(dataResults.filter((row) => {
        return selectedHrchyGrp.indexOf(row.hrchyGrp) !== -1;
      }));
    } else {
      setFilteredItemHrchies(dataResults);
    }

    DataState.initialize(gridRef.current?.api);
  }

  const retrieveHrchyConfigs = () => {
    hrchyConfigService.getAll({ searchFilters: { cd: null, itemHrchyFlg: true } })
      .then(response => {
        const { results } = response.data;
        if (isEmpty(results)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        }

        const grpCombo = results.map((d) => {
          return { cd: d.grpCd, name: d.grpName };
        }).filter((x, idx, arr) => {
          return arr.findIndex((item) => item.name === x.name && item.cd === x.cd) === idx
        });

        const hrchyGrpConfig = results.map((d) => {
          return d;
        }).filter((x, idx, arr) => {
          return arr.findIndex((item) => item.grpName === x.grpName && item.grpCd === x.grpCd) === idx
        });

        setHrchyGrp(grpCombo);
        setHrchyGrpConfigs(hrchyGrpConfig);
        setHrchyLvConfigs(results);
      }).catch(e => {
        debug(e);
      })
  }

  const hrchyLvConfigsCombo = (params, hrchyLevelConfigs) => {
    return hrchyLevelConfigs.map((d) => {
      return d;
    }).filter((x) => {
      return x.grpCd === params.grpCd;
    });
  }

  const retrieveItemHrchies = () => {
    const params = getRequestParams();
    setItemHrchies([]);
    itemHrchyService.getAll(params)
      .then(response => {
        const { results } = response.data;
        setItemHrchies(results);
        refreshGridByFilter(results);
      })
      .catch(e => {
        debug(e);
      })
      .then(() => {
        const reduxState = reduxUtil.createObjectState(viewName, { searchCode: searchCode.current, advancedFilters });
        reduxDispatch(reduxUtil.updateViewState(reduxState));
        DataState.initialize(gridRef.current?.api);
      });
  }

  const checkDisaggregationRatio = () => {
    const error = [];
    if (filteredItemHrchies === null || filteredItemHrchies.length === 0) {
      return error;
    }

    const mappedByParent = filteredItemHrchies.reduce((acc, item) => {
      const key = item.parentHrchyCd;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {});
    //parent hrchy cd로 groupby 후 check
    for (const parentHrchyCd of Object.keys(mappedByParent)) {
      const children = mappedByParent[parentHrchyCd];
      if (children?.length <= 0) {
        continue;
      }
      let totalDisaggRatio = null;
      for (const child of children) {
        if (child.disaggRatio === null) {
          continue;
        }
        totalDisaggRatio = NumberUtil.decimalAdd(totalDisaggRatio, (child.disaggRatio * 100 || 0));
      }
      if (totalDisaggRatio !== null && (totalDisaggRatio > planConfig?.objVal?.max || totalDisaggRatio < planConfig?.objVal?.min)) {
        //error 발생
        const hrchyCds = children.map(child => child.hrchyCd);
        error.push(...hrchyCds);
      }
    }
    return error;
  }

  const saveItemHrchy = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    if (allStateData.length > 0) {
      const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};
      for (const row of allStateData) {
        if (row.rootFlg) {
          for (const data of filteredItemHrchies) {
            if (data?.parentHrchy === null) {
              allStateData.push({...data});
            }
          }
        }
      }
      
      const dataWithFlg = allStateData.map((row) => {
        const originalData = updatedOrigin[row.rowIndex];
        let activeFlgChanged = false;
        let leafFlgChanged = false;
        if (originalData) {
          activeFlgChanged = originalData?.activeFlg !== row?.activeFlg;
          leafFlgChanged = originalData?.leafFlg !== row?.leafFlg;
        }
        
        return { ...row, activeFlgChanged, leafFlgChanged };
      });
      
      setProgressOn(true);
      itemHrchyService.upsert(dataWithFlg)
        .then(() => {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") });
        })
        .catch(e => {
          const { message } = e.response?.data;
          if (message) {
            setSnackInfo({ open: true, severity: message.type, content: t(message.message) });
          } else {
            setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") });
          }
          debug(e);
        })
        .then(() => {
          retrieveItemHrchies();
        })
        .finally(() => {
          setProgressOn(false);
        });
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  }

  const chkRows = () => {
    const chkEmptyRows = [];
    gridRef.current.api.stopEditing(false);
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {
        if (!data.id) {
          chkEmptyRows.push(data);
        }
      }
    );

    gridRef.current.api.applyTransaction({ remove: chkEmptyRows });
    setDialogInfo({ open: true, title: "delete", content: `${t("MSG_DeleteDataAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.itemHrchy, 'delete')}` })
  }

  const removeItemHrchy = () => {
    const itemHrchyIds = [];
    gridRef.current.api.stopEditing();
    gridRef.current.api.getSelectedRows().forEach(
      (data) => {

        itemHrchyIds.push(data.id)
      }
    );

    setProgressOn(true);
    itemHrchyService.remove(itemHrchyIds)
      .then(() => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") });
      })
      .catch(e => {
        setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") });
        debug(e);
      })
      .then(() => {
        retrieveItemHrchies();
      })
      .finally(() => {
        setProgressOn(false)
      });
  }

  const handleDialog = (action) => {
    if (action === "SAVE") {
      saveItemHrchy();
    } else if (action === "DELETE") {
      removeItemHrchy();
    }
  }

  const handleChange = (event) => {
    const values = event.target.value;
    setSelectedHrchyGrp(values);

    const selected = [];
    for (const value of values) {
      selected.push(hrchyGrp[findIndex(hrchyGrp, { id: value })]);
    }
    setSelectedDataHrchyGrp(selected);
  }

  const handleSelect = e => {
    if (e.target?.nodeName.toUpperCase() !== "LI") {
      setHrchyGrpSelectorOpen(!hrchyGrpSelectorOpen);
    }
  }

  const handleDelete = (value) => {
    const currentHrchyGrp = [...selectedHrchyGrp];
    const currentDataHrchyGrp = [...selectedDataHrchyGrp];
    const removeIndex = currentHrchyGrp.indexOf(value);
    if (removeIndex > - 1) {
      currentHrchyGrp.splice(removeIndex, 1);
      currentDataHrchyGrp.splice(removeIndex, 1);
      setSelectedHrchyGrp(currentHrchyGrp);
      setSelectedDataHrchyGrp(currentDataHrchyGrp);
    }
  }

  const MenuProps = {
    PaperProps: {
      style: {
        maxHeight: 48 * 4.5 + 8,
        width: 250,
      },
    },
  };

  const addLv1Row = () => {
    const newhrchyGrp = Object.keys(hrchyGrpConfigs)[0];
    const newRow = {
      hrchyPath: [`code${new Date().getMilliseconds()}`],
      level: 1,
      hrchyConfig: Object.keys(hrchyGrpConfigs).length > 0 ? hrchyGrpConfigs[newhrchyGrp][0] : null,
      hrchyLvCd: null,
      name: null,
      descTxt: null,
      activeFlg: true,
      verNum: 0
    };

    const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addNewRow);
  };

  const selectLevelDialogOpen = () => {
    gridRef.current.api.stopEditing();
    const selectedRow = gridRef.current.api.getSelectedRows()[0];
    if (selectedRow?.id) {
      setCurrentHrchy(selectedRow);
      setSelectLevelDialog(true);
    } else {
      addLv1Row();
    }
  }

  const selectLevelDialogClose = () => {
    setSelectLevelDialog(false);
  }

  const setRowLevelData = () => {
    const rowData = gridRef.current.api.getSelectedRows()[0] ? gridRef.current.api.getSelectedRows()[0] : { hrchyLv: 1 };
    setCurrentHrchy(rowData);
  }

  const selectLevel = (data) => {
    if (data.level === 1) {
      addLv1Row();
      setSelectLevelDialog(false);
    } else {
      const path = data.hrchyPath.slice();
      path.pop();
      path.push(`code${new Date().getMilliseconds()}`);

      const newRow = {
        hrchyPath: path,
        parentHrchyCd: data.parentHrchyCd,
        hrchyConfig: data.hrchyConfig,
        name: null,
        descTxt: null,
        activeFlg: true,
        rootFlg: false,
        verNum: 0
      };

      const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
      DataState.setDataState(addNewRow);
      setSelectLevelDialog(false);
    }
  }

  const selectHighLevel = (data) => {
    const path = data.hrchyPath.slice();
    path.pop();
    path.push(`code${new Date().getMilliseconds()}`);

    const params = {
      itemHrchyFlg: data.hrchyConfig.itemHrchyFlg,
      grpCd: data.hrchyConfig.grpCd
    }
    hrchyConfigService.getTopHrchy(params)
    .then((response) => {
      if (response?.data?.data) {
        const topHrchy = response.data.data.cd;
        if( topHrchy === data.hrchyConfig.cd) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_NoTopHrchyConfig") });
        } else {
          const newRow = {
            hrchyPath: path,
            parentHrchyCd: null,
            name: null,
            hrchyConfig: response.data.data,
            descTxt: null,
            activeFlg: true,
            rootFlg: true,
            verNum: 0
        };
        const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
        DataState.setDataState(addNewRow);
        setSelectLevelDialog(false);
        }
      }
    })
    .catch((e) => {
      debug(e);
    });
  }


  const selectLowLevel = (data) => {
    const path = data.hrchyPath.slice();
    path.push(`code${new Date().getMilliseconds()}`);

    const newRow = {
      hrchyPath: path,
      parentHrchyCd: data.hrchyCd,
      name: null,
      hrchyConfig: data.hrchyConfig,
      descTxt: null,
      activeFlg: true,
      rootFlg: false,
      verNum: 0
    };

    const addNewRow = gridRef.current.api.applyTransaction({ add: [newRow], addIndex: 0 }).add[0];
    DataState.setDataState(addNewRow);
    setSelectLevelDialog(false);
  }

  const grpKeyCreator = (params) => {
    return params.value.grpCd;
  }

  const lvKeyCreator = (params) => {
    return params.value.cd;
  }

  const hrchyGrpConfigValueFormatter = (hrchyConfig) => {
    const { value } = hrchyConfig;
    const formatValue = value?.grpName ? value.grpName : value;

    return formatValue ? formatValue : "";
  }

  const hrchyLvConfigCellEditor = (hrchyConfig) => {
    const { value } = hrchyConfig;
    const formatValue = value?.name ? value.name : value;

    return formatValue ? formatValue : "";
  }

  const grpValueFormatter = (params) => {
    const filterVal = hrchyLvConfigs.filter(arr => arr.grpCd === params.value)
    return filterVal.length > 0 ? filterVal[0].grpName : null;
  }

  const lvValueFormatter = (params) => {
    const filterVal = hrchyLvConfigs.filter(arr => arr.cd === params.value)
    return filterVal.length > 0 ? filterVal[0].name : null;
  }

  const numberSetter = (params) => {
    if (params?.newValue === "") {
      params.data.disaggRatio = null;
      return true;
    }
    const newValue = parseFloat(params.newValue);
    // 유효성 검사: 숫자이고 음수가 아니어야 함
    if (isNaN(newValue) || newValue < 0) {
      return false; // 값 변경 거부
    }
    if (params.data?.disaggRatio !== newValue) {
      params.data.disaggRatio = newValue;
      return true;
    }
    return false;
  }

  const collapseFilter = _event => {
    setOpenFilter(!openFilter);
  };

  const columnDefs = [
    { headerName: "hrchyPath", field: "hrchyPath", hide: true, type: ["defaultValueFormatter", "defaultValueParser"], suppressColumnsToolPanel: true },
    { headerName: "level", field: "level", hide: true, suppressColumnsToolPanel: true },
    { headerName: "parentHrchyCd", field: "parentHrchyCd", hide: true, suppressColumnsToolPanel: true, cellClass: 'stringType' }
  ];

  const excelColumnDef = [
    { headerName: "hrchyCd", field: "hrchyCd", cellClass: 'stringType', filterType: "string" }
  ];

  const exceptionCellStyle = params => {
    if (planConfig === null) {
      return {};
    }
    const errorHrchy = checkDisaggregationRatio();
    if (!isEmpty(errorHrchy) && errorHrchy.includes(params.data.hrchyCd)) {
      return { color: red[700], fontWeight: 'bold' }; // Red background for out of range values
    }
    return {};
  }

  const commonDefs = [
    {
      headerName: "hrchyGrpName", field: "hrchyConfig",
      cellEditor: "agRichSelectCellEditor", 
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: hrchyGrpConfigValueFormatter,
      keyCreator: grpKeyCreator,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: grpValueFormatter
      },
      cellEditorParams: {
        valueFormatter: hrchyGrpConfigValueFormatter,
        values: hrchyGrpConfigs
      },
      comparator: (value1, value2) => {
        const val1 = !value1 ? "" : value1.name;
        const val2 = !value2 ? "" : value2.name;
        if (val1 === val2) {
          return 0;
        } else if (val1 > val2) {
          return 1;
        }
        return -1;
      },
      minWidth: 140,
      flex: 13,
      filterType: 'string',
      context: { pasteOption: hrchyGrpConfigs },
      filterOption: hrchyGrpConfigs.map(cfg => cfg.grpName)
    },
    {
      headerName: "hrchyLvName", field: "hrchyConfig",
      cellEditor: "agRichSelectCellEditor",
      cellEditorPopup: true,
      type: ["defaultValueParser"],
      valueFormatter: hrchyLvConfigCellEditor,
      keyCreator: lvKeyCreator,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: lvValueFormatter
      },
      cellEditorParams: (params) => {
        return {
          valueFormatter: hrchyLvConfigCellEditor,
          values: hrchyLvConfigsCombo(params.value, hrchyLvConfigs)
        }
      },
      comparator: (value1, value2) => {
        const val1 = !value1 ? "" : value1.name;
        const val2 = !value2 ? "" : value2.name;
        if (val1 === val2) {
          return 0;
        } else if (val1 > val2) {
          return 1;
        }
        return -1;
      },
      minWidth: 140,
      flex: 13,
      filterType: 'string',
      context: { pasteOption: hrchyLvConfigs },
      filterOption: hrchyLvConfigs.map(cfg => cfg.name)
    },
    { headerName: "hrchyName", field: "name", minWidth: 140, flex: 14, filterType: 'string' },
    { headerName: "descTxt", field: "descTxt", filterType: 'string' },
    { headerName: "targetFillRate", field: "targetFillRate", type: ["numberColumn", "percentColumn", "rightAligned"], filterType: 'percent' },
    // v26.10: Channel Planning 미출시 — 열만 내린다. 행 데이터엔 값이 남아 저장해도 DB 값이 보존된다
    ...(CodeUtil.SHOW_CHANNEL ? [{ headerName: "targetInvDays", field: "targetInvDays", type: ["numberColumn", "rightAligned"], filterType: 'number' }] : []),
    { headerName: "disaggRatio", field: "disaggRatio", valueSetter: (params) => { numberSetter(params); },
      type: ["numberColumn", "percentColumn", "rightAligned"], cellStyle: exceptionCellStyle, filterType: 'percent' },
    { headerName: "activeFlg", field: "activeFlg", type: ["booleanColumn"], minWidth: 80, flex: 8, filterType: 'boolean' },
    { headerName: "leafFlg", field: "leafFlg", type: ["booleanColumn"], minWidth: 70, flex: 8, filterType: 'boolean' },
    { headerName: "createdTs", field: "createdTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 11, filterType: 'timestamp' },
    { headerName: "createdBy", field: "createdUser", colId: "createdBy", type: "nonEditableColumn", width: 150, filterType: 'string' },
    { headerName: "updatedTs", field: "updatedTs", type: ["nonEditableColumn"], valueFormatter: dateUtils.formatDateTime, minWidth: 170, flex: 11, filterType: 'timestamp' },
    { headerName: "updatedBy", field: "updatedUser", colId: "updatedBy", type: "nonEditableColumn", width: 150, filterType: 'string' }
  ];

  const checkActiveFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.activeFlg !== row.activeFlg && row.activeFlg === false;
      }
      return false;
    });
  };

  const checkLeafFlgChanges = () => {
    const allStateData = DataState.getAllStateData(gridRef.current.api);
    const updatedOrigin = DataState.getUpdateOrigin(gridRef.current.api) || {};

    return allStateData.some((row) => {
      const originalData = updatedOrigin[row.rowIndex];
      if (originalData) {
        return originalData.leafFlg !== row.leafFlg && row.leafFlg === false;
      }
      return false;
    });
  };
  
  const handleSaveClick = () => {
    gridRef.current?.api?.stopEditing();
      
    const hasActiveFlgChanges = checkActiveFlgChanges();
    const hasLeafFlgChanges = checkLeafFlgChanges();
      
    if (hasActiveFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.itemHrchy, 'deactivate')}` }); 
    } else if (hasLeafFlgChanges) {
      setDialogInfo({ open: true, title: "save", content: `${t("MSG_ActiveStatusChangedAssociated")}\n\n${CodeUtil.makeMessage(CodeUtil.deleteRelations.itemHrchy, 'leafFalse')}` });
    } else {
      setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") });
    }
  };

  const onGridReady = params => {
    retrieveHrchyConfigs();
    GridUtils.setColumnDefs({ ...params, columnDefs: [...columnDefs, ...commonDefs], viewName, initState: true });
  }

  const getCsvColDefs = () => {
    const csvColDefs = cloneDeep([...excelColumnDef, ...commonDefs]);
    return csvColDefs;
  }

  useEffect(() => {
    retrievePlanConfig();
    DataState.initialize(gridRef.current?.api);
    retrieveItemHrchies();

    return () => {
      setItemHrchies([]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  useEffect(() => {
    refreshGridByFilter(itemHrchies);
    // eslint-disable-next-line
  }, [selectedHrchyGrp]);

  useEffect(() => {
    if (!isEmpty(hrchyGrpConfigs) || !isEmpty(hrchyLvConfigs)) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...columnDefs, ...commonDefs] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrchyGrpConfigs, hrchyLvConfigs]);

  useEffect(() => {
    if (itemHrchies) {
      GridUtils.setColumnDefs({ ...gridRef.current, columnDefs: [...columnDefs, ...commonDefs] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfig, itemHrchies]);

  useEffect(() => {
    if (!isEmpty(advancedFilters) && advancedFilters?.children.length > 0) {
      setAdvancedFilterColor("info");
    } else {
      setAdvancedFilterColor("default");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFilters]);

  return (
    <>
      <FilterContainer>
      <TextFieldSearch sx={{ width: 250, mr: 1 }} label={"itemHrchyCd"} searchCode={searchCode} retrieve={retrieveItemHrchies}/>
        <FormControl size="small" sx={{ width: 250 }}>
          <InputLabel id="hrchyGrp">{t("hrchyGrpName")}</InputLabel>
          <Select
            labelId="grid-hrchyGrp-multiple-chip-label"
            id="grid-hrchyGrp-multiple-chip"
            multiple
            value={selectedHrchyGrp}
            open={hrchyGrpSelectorOpen}
            onChange={handleChange}
            onClick={handleSelect}
            input={<OutlinedInput id="select-multiple-chip" label={t("hrchyGrpName")} />}
            renderValue={(selected) => (
              <Box>
                {selected.map((value) => (
                  <Chip size="small"
                    key={value}
                    label={hrchyGrp[findIndex(hrchyGrp, { cd: value })].name}
                    onDelete={() => handleDelete(value)}
                  />
                ))}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {hrchyGrp.map((row) => (
              <MenuItem key={row.cd} value={row.cd}>{row.name}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box sx={{ ml: "auto" }}>
          <ExcelExportButton title={title} service={itemHrchyService} params={getRequestParams()} columnDefs={getCsvColDefs()} />
          <AddButton onClick={selectLevelDialogOpen} />
          <RemoveButton onClick={chkRows} />
          <SaveButton onClick={handleSaveClick} />
          <SettingsButton label="settings" size="medium" onClick={() => setConfigModalOpen(true)} />
          <FilterButton label="advancedFilter" advancedFilterColor={advancedFilterColor} onClick={collapseFilter} />
        </Box>
      </FilterContainer>
      <Box className="ag-theme-balham grid-area" sx={{ pb: 1 }}>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'row', height: '100%', mr: 1.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <AgGridReact
              ref={gridRef}
              rowData={filteredItemHrchies}
              {...defaultGridMemo}
              rowSelection={{
                ...defaultGridMemo.rowSelection,
                headerCheckbox: true,
                checkboxes: true,
              }}
              autoGroupColumnDef={autoGroupColumnMemo}
              treeData
              groupDefaultExpanded={-1}
              onGridReady={onGridReady}
              getDataPath={(data) => data.hrchyPath}
              onCellClicked={setRowLevelData}
              enterNavigatesVertically
            >
            </AgGridReact>
          </Box>
          {openFilter && (
            <ResizableFilterPanel>
              <AdvancedFilter
                columnDefs={[...excelColumnDef, ...commonDefs]}
                advancedFilters={advancedFilters}
                onApply={setAdvancedFilters}
                onClose={() => setOpenFilter(false)}
              />
            </ResizableFilterPanel>
          )}
        </Box>
      </Box>
      <Backdrop sx={{ color: '#fff', zIndex: 10000 }} open={progressOn}>
        <CircularProgress color="inherit" size={"80px"} />
      </Backdrop>
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <RatioRangeSettings
        viewName={viewName}
        open={configModalOpen}
        setOpen={setConfigModalOpen}
        setPlanConfig={setPlanConfig}
        planConfig={planConfig}
      />
      <BaseConfigModal open={selectLevelDialog} onClose={selectLevelDialogClose} title={t("selectHrchyLevel")}>
        <List sx={{ pt: 0 }}>
          {currentHrchy.parentName === null ?
            <ListItem key={`${currentHrchy.level - 1}`}>
              <ListItemButton onClick={() => selectHighLevel(currentHrchy)} >
                <ListItemText primary={`Level ${currentHrchy.level - 1} (Parent: ${currentHrchy.parentName === null ? t("rootNodes") : currentHrchy.parentName})`} sx={{ textAlign: "center" }} />
              </ListItemButton>
            </ListItem>
            : <Box></Box>}
          <ListItem key={currentHrchy.level}>
            <ListItemButton onClick={() => selectLevel(currentHrchy)} >
              <ListItemText primary={`Level ${currentHrchy.level} (Parent: ${currentHrchy.parentName === null ? t("none") : currentHrchy.parentName})`} sx={{ textAlign: "center" }} />
            </ListItemButton>
          </ListItem>
          <ListItem key={`${currentHrchy.level + 1}`}>
            <ListItemButton onClick={() => selectLowLevel(currentHrchy)} >
              <ListItemText primary={`Level ${currentHrchy.level + 1} (Parent: ${currentHrchy.name})`} sx={{ textAlign: "center" }} />
            </ListItemButton>
          </ListItem>
        </List>
      </BaseConfigModal>
    </>
  );
};

export default withTranslation()(ItemHrchyMaster);
